/**
 * A workspace's own logo, palette and name, applied at runtime.
 *
 * The theme already lives in CSS custom properties (`--primary` and friends in
 * index.css), and every component reads them through Tailwind. So rebranding
 * does not touch a single component: it overwrites those variables on
 * `document.documentElement` and the whole app follows. Clearing them puts the
 * product's own palette back, because the stylesheet is still the fallback.
 *
 * The variables hold bare HSL triples ("214 94% 49%"), not colours, so that
 * Tailwind can compose them with an alpha channel. `hexToHslTriple` below is the
 * only reason this file knows anything about colour maths.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../services/api';
import { useAuth } from './AuthContext';

const BrandingContext = createContext(null);

/** "#0f766e" -> "175 84% 26%" — the shape the CSS variables already use. */
export const hexToHslTriple = (hex) => {
  if (typeof hex !== 'string') return null;
  let value = hex.trim().replace('#', '');
  if (value.length === 3) value = value.split('').map((c) => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(value)) return null;

  const r = parseInt(value.slice(0, 2), 16) / 255;
  const g = parseInt(value.slice(2, 4), 16) / 255;
  const b = parseInt(value.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0));
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

/** Which branding field drives which CSS variable. */
const VAR_MAP = {
  primary_color: ['--primary'],
  primary_hover_color: ['--primary-hover'],
  // `secondary_color` predates the palette work and is the darker brand tone, so
  // it stands in for the hover state when nothing more specific is set.
  accent_color: ['--accent-foreground'],
};

const applyPalette = (brand) => {
  const root = document.documentElement;
  Object.entries(VAR_MAP).forEach(([field, cssVars]) => {
    const triple = hexToHslTriple(brand?.[field]);
    cssVars.forEach((name) => {
      // Removing rather than setting a default: the stylesheet's own value is
      // the fallback, so an unbranded workspace renders byte-identically to how
      // it did before any of this existed.
      if (triple) root.style.setProperty(name, triple);
      else root.style.removeProperty(name);
    });
  });
  if (brand?.sidebar_color) root.style.setProperty('--brand-sidebar', brand.sidebar_color);
  else root.style.removeProperty('--brand-sidebar');
};

/**
 * A workspace can pin light or dark for everyone in it. `system` — the default,
 * and what every workspace does today — removes the pin and hands the decision
 * back to the device, so nothing changes for anyone who has not chosen.
 */
const applyThemeMode = (mode) => {
  const root = document.documentElement;
  if (mode === 'light' || mode === 'dark') {
    root.setAttribute('data-theme', mode);
    root.classList.toggle('dark', mode === 'dark');
  } else {
    root.removeAttribute('data-theme');
    const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
    root.classList.toggle('dark', Boolean(prefersDark));
  }
};

const applyFavicon = (url) => {
  if (!url) return;
  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = url;
};

export const BrandingProvider = ({ children }) => {
  // `user`, not an `isAuthenticated` flag — AuthContext exposes the former and
  // reading the latter silently disabled every fetch in this provider.
  const { user } = useAuth();
  // Only the workspace matters here, and depending on the whole `user` object
  // would refetch the theme on every unrelated auth-state change.
  const tenantId = user?.tenantId ?? null;
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!tenantId) {
      setBranding(null);
      applyPalette(null);
      applyThemeMode('system');
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get('/api/branding/theme');
      setBranding(data);
      applyPalette(data);
      applyThemeMode(data?.theme_mode);
      applyFavicon(data?.favicon_url);
      if (data?.app_name) document.title = data.app_name;
    } catch {
      // A workspace with no branding, or a request that failed, must not blank
      // the app — the stylesheet's own palette is already on screen.
      setBranding(null);
      applyPalette(null);
      applyThemeMode('system');
    } finally {
      setLoading(false);
    }
    // Re-fetch when the workspace changes: switching location is a different
    // tenant, and therefore possibly a different brand.
  }, [tenantId]);

  useEffect(() => { refresh(); }, [refresh]);

  const value = useMemo(
    () => ({
      branding,
      loading,
      refresh,
      appName: branding?.app_name || null,
      logoUrl: branding?.logo_url || null,
      logoDarkUrl: branding?.logo_dark_url || branding?.logo_url || null,
      whiteLabel: Boolean(branding?.white_label_enabled),
      hidePlatformBranding: Boolean(branding?.hide_platform_branding),
    }),
    [branding, loading, refresh],
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
};

export const useBranding = () => useContext(BrandingContext) || {
  branding: null, loading: false, refresh: () => {},
  appName: null, logoUrl: null, logoDarkUrl: null,
  whiteLabel: false, hidePlatformBranding: false,
};

export default BrandingContext;
